/*
 * This file is part of ng2-json-editor.
 * Copyright (C) 2016 CERN.
 *
 * ng2-json-editor is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License as
 * published by the Free Software Foundation; either version 2 of the
 * License, or (at your option) any later version.
 *
 * ng2-json-editor is distributed in the hope that it will be useful, but
 * WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with ng2-json-editor; if not, write to the Free Software Foundation, Inc.,
 * 59 Temple Place, Suite 330, Boston, MA 02111-1307, USA.
 * In applying this license, CERN does not
 * waive the privileges and immunities granted to it by virtue of its status
 * as an Intergovernmental Organization or submit itself to any jurisdiction.
*/

import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Set } from 'immutable';
import { Subscription } from 'rxjs/Subscription';

import { AbstractSubscriberComponent } from '../abstract-subscriber';
import { DomUtilService, PathUtilService, KeysStoreService, JsonSchemaService } from '../shared/services';
import { JSONSchema } from '../shared/interfaces';


export class AddNestedFieldDropdownComponent extends AbstractSubscriberComponent implements OnChanges, OnDestroy {

   schema: JSONSchema;
   pathString: string;
   isDisabled: boolean;

  nestedKeysMap: { [path: string]: Set<string> };
  keysChangeSubscription: Subscription;

  constructor(public keysStoreService: KeysStoreService,
    public jsonSchemaService: JsonSchemaService,
    public pathUtilService: PathUtilService,
    public domUtilService: DomUtilService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    const pathStringChange = changes['pathString'];
    if (pathStringChange) {
      this.nestedKeysMap = {};
      this.nestedKeysMap[this.pathString] = this.keysStoreService.keysMap[this.pathString];
      const nestedPathPrefix = this.pathString + this.pathUtilService.separator;
      Object.keys(this.keysStoreService.keysMap)
        .filter(path => path.startsWith(nestedPathPrefix))
        .forEach(path => {
          this.nestedKeysMap[path] = this.keysStoreService.keysMap[path];
        });

      if (this.keysChangeSubscription) {
        this.keysChangeSubscription.unsubscribe();
      }
      this.keysChangeSubscription = this.keysStoreService.onKeysChange
        .filter(change => change.path.startsWith(this.pathString))
        .takeUntil(this.isDestroyed)
        .subscribe(change => { this.nestedKeysMap[change.path] = change.keys; });
    }
  }

  /**
   * Return keys that could be added for the given path.
   *
   * @return schema key - hidden keys - existing keys
   */
  addableKeysForPath(path: string): Set<string> {
    const keys = this.nestedKeysMap[path];
    const schema = this.jsonSchemaService.forPathString(path);
    // || schema.items.properties is to handle the keys when the path belongs to table-list.
    const schemaProps = schema.properties || schema.items.properties;
    const schemaKeys = Set<string>(Object.keys(schemaProps)
      .filter(key => !schemaProps[key].hidden));
    const addableKeys = schemaKeys.subtract(keys);
    return addableKeys.size > 0 ? addableKeys : undefined;
  }

  onFieldSelect(path: string, key: string) {
    let schema = this.jsonSchemaService.forPathString(path);
    if (schema.componentType === 'table-list') {
      schema = schema.items;
    }
    const newKeyPath = this.keysStoreService.addKey(path, key, schema);
    if (this.keysStoreService.keysMap[newKeyPath]) {
      this.nestedKeysMap[newKeyPath] = this.keysStoreService.keysMap[newKeyPath];
    }
    this.domUtilService.focusAndSelectFirstEditableChildById(newKeyPath);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'add-nested-field-dropdown',
  styles: [".btn-add-field-dropdown { padding: 0 3px; font-size: 11px; opacity: 0.4; border: 0; background: transparent; font-weight: bold; line-height: 1; text-shadow: 0 1px 0 #fff; color: darkslategray; margin-bottom: 2px; float: left; } .btn-add-field-dropdown:hover { color: blue; opacity: 0.6; } .dropdown-filter-container { padding: 0 3px; } .dropdown-filter-container input { width: 100%; } ",
".dropdown-header.title { font-size: 13.5px; font-weight: bold; padding-left: 10px; } .dropdown-divider { margin: 4px 0; } .dropdown-menu { left: auto; right: 0; } .dropdown-menu li > .dropdown-item { display: block; padding: 3px 20px; clear: both; font-weight: normal; line-height: 1.42857; color: #333333; white-space: nowrap; } .dropdown-menu li > .dropdown-item:hover { text-decoration: none; color: #262626; background-color: #f5f5f5; } "],
  template: "<div class=\"btn-group add-field-dropdown-container\" dropdown keyboardNav=\"true\" [isDisabled]=\"isDisabled\"> <button type=\"button\" class=\"btn btn-add-field-dropdown\" dropdownToggle> <i class=\"fa fa-plus\"></i> <i class=\"fa fa-caret-down\"></i> </button> <ul class=\"dropdown-menu\" *dropdownMenu> <div *ngFor=\"let path of nestedKeysMap | keys | sortAlphabetically; first as isFirst\"> <div *ngIf=\"addableKeysForPath(path); let addableKeys\"> <li *ngIf=\"!isFirst\" class=\"divider dropdown-divider\"></li> <li *ngIf=\"!isFirst\" class=\"dropdown-header title\">{{path | lastPathElement}}</li> <li *ngFor=\"let key of addableKeys | sortAlphabetically\"> <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"onFieldSelect(path, key)\">{{key}}</a> </li> </div> </div> </ul> </div>",
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: KeysStoreService, },
{type: JsonSchemaService, },
{type: PathUtilService, },
{type: DomUtilService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'schema': [{ type: Input },],
'pathString': [{ type: Input },],
'isDisabled': [{ type: Input },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
